
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Users, Tag } from "lucide-react";
import CommunityHelpersHeader from "./community-helpers/CommunityHelpersHeader";
import CategoriesTab from "./community-helpers/CategoriesTab";
import HelpersTab from "./community-helpers/HelpersTab";
import CommunityHelpersDialogs from "./community-helpers/CommunityHelpersDialogs";
import { useCommunityHelpersData } from "./community-helpers/useCommunityHelpersData";

interface CommunityHelperCategory {
  id: string;
  name: string;
  color: string;
  display_order: number;
  is_active: boolean;
}

interface CommunityHelper {
  id: string;
  name: string;
  description: string;
  image_url: string;
  tools: string[];
  uniform: string;
  workplace: string;
  fun_fact: string;
  category_id: string | null;
  display_order: number;
  is_active: boolean;
  community_helper_categories?: { name: string };
}

const CommunityHelpersManager = () => {
  const {
    categories,
    helpers,
    filteredHelpers,
    selectedCategoryFilter,
    setSelectedCategoryFilter,
    loading,
    fetchData,
    handleDeleteCategory,
    handleDeleteHelper,
  } = useCommunityHelpersData();

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [helperDialogOpen, setHelperDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CommunityHelperCategory | undefined>();
  const [selectedHelper, setSelectedHelper] = useState<CommunityHelper | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleCategorySubmit = async (data: Omit<CommunityHelperCategory, 'id'>) => {
    setIsSubmitting(true);
    try {
      if (selectedCategory) {
        const { error } = await supabase
          .from("community_helper_categories")
          .update(data)
          .eq("id", selectedCategory.id);

        if (error) throw error;
        toast({ title: "Category updated successfully" });
      } else {
        const { error } = await supabase
          .from("community_helper_categories")
          .insert([data]);

        if (error) throw error;
        toast({ title: "Category created successfully" });
      }

      await fetchData();
      closeDialogs();
    } catch (error) {
      console.error("Error saving category:", error);
      toast({
        title: "Error",
        description: "Failed to save category",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeDialogs = () => {
    setCategoryDialogOpen(false);
    setHelperDialogOpen(false);
    setSelectedCategory(undefined);
    setSelectedHelper(undefined);
  };

  const handleSuccess = () => {
    fetchData();
    closeDialogs();
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <CommunityHelpersHeader title="Community Helpers Management" />

      <Tabs defaultValue="categories" className="space-y-4">
        <TabsList>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="helpers" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Community Helpers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="categories">
          <CategoriesTab
            categories={categories}
            onAddCategory={() => {
              setSelectedCategory(undefined);
              setCategoryDialogOpen(true);
            }}
            onEditCategory={(category) => {
              setSelectedCategory(category);
              setCategoryDialogOpen(true);
            }}
            onDeleteCategory={handleDeleteCategory}
          />
        </TabsContent>

        <TabsContent value="helpers">
          <HelpersTab
            helpers={filteredHelpers}
            categories={categories}
            selectedCategoryFilter={selectedCategoryFilter}
            onCategoryFilterChange={setSelectedCategoryFilter}
            onAddHelper={() => {
              setSelectedHelper(undefined);
              setHelperDialogOpen(true);
            }}
            onEditHelper={(helper) => {
              setSelectedHelper(helper);
              setHelperDialogOpen(true);
            }}
            onDeleteHelper={handleDeleteHelper}
          />
        </TabsContent>
      </Tabs>

      <CommunityHelpersDialogs
        categoryDialogOpen={categoryDialogOpen}
        helperDialogOpen={helperDialogOpen}
        selectedCategory={selectedCategory}
        selectedHelper={selectedHelper}
        isSubmitting={isSubmitting}
        onCategoryDialogClose={() => setCategoryDialogOpen(false)}
        onHelperDialogClose={() => setHelperDialogOpen(false)}
        onCategorySubmit={handleCategorySubmit}
        onHelperSuccess={handleSuccess}
        onCancel={closeDialogs}
      />
    </div>
  );
};

export default CommunityHelpersManager;
